import * as React from "react";
import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  TopToolbar,
  CreateButton,
  EditButton,
  usePermissions,
  useListContext,
  useRedirect,
  FunctionField,
  ListBase,
  useDataProvider,
  useNotify,
  useRefresh,
  useDelete,
  DeleteButton,
  Pagination,
  TextInput,
} from "react-admin";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  IconButton,
  Box,
  useMediaQuery,
  Avatar,
  Chip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip,
  TextField as MuiTextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useClub } from "../club/ClubContext";
import { can } from "../auth/can";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import PlayerIdentityRow from "../profile/PlayerIdentityRow";

/* -------------------------------- helpers -------------------------------- */

const makeAbsolute = (url?: string | null) => {
  if (!url) return undefined;
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_CDN_URL ?? ""}${url}`;
};

const initials = (r: any) => {
  const name = `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim();
  const base = name || r.email || "?";
  return base
    .split(" ")
    .map((p: string) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

/* ------------------------------- List Actions ------------------------------ */

const ListActions = () => {
  const { permissions, isLoading } = usePermissions();
  if (isLoading) return null;

  return (
    <TopToolbar>
      {can(permissions, "members", "create") && <CreateButton />}
    </TopToolbar>
  );
};

const StatusChip = ({ status }: { status?: string | null }) => (
  <Chip
    size="small"
    variant="outlined"
    label={status ?? "—"}
    sx={{ height: 22, fontSize: 12 }}
  />
);

const userFilters = [
  <TextInput key="q" source="q" label="Suche" alwaysOn />,
];

function MobileSearchBar() {
  const { filterValues, setFilters } = useListContext();
  const [q, setQ] = React.useState<string>(filterValues?.q ?? "");

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setFilters({ ...filterValues, q: q || undefined }, undefined);
    }, 350);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <Box sx={{ px: 1, pb: 0.5 }}>
      <MuiTextField
        size="small"
        placeholder="Name oder E-Mail suchen..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        fullWidth
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />,
        }}
      />
    </Box>
  );
}

/* ----------------------------- Desktop Resend Button ------------------------ */

function ResendInviteIconButton({ record }: { record: any }) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // Optional: nur aktivieren, wenn es nach "pending/invite" aussieht
  const status = (record?.status ?? "").toString().toLowerCase();
  const looksInvitable =
    status.includes("invite") || status.includes("pending") || status === "invited";

  const onClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await dataProvider.create("users/resendInvite", {
        data: { userId: record.id },
      });
      notify("Einladungslink wurde erneut gesendet.", { type: "info" });
      refresh();
    } catch (err: any) {
      const msg =
        err?.body ? JSON.stringify(err.body, null, 2) : err?.message ?? "Fehler";
      notify(msg, { type: "warning" });
    }
  };

  return (
    <Tooltip title="Einladung erneut senden">
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={!looksInvitable}
          aria-label="Einladung erneut senden"
        >
          <MailOutlineIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}

/* ----------------------------- Mobile Actions Menu -------------------------- */

function UserActionsMenu({
  record,
  allowResend,
  allowEdit,
  allowDelete,
}: {
  record: any;
  allowResend: boolean;
  allowEdit: boolean;
  allowDelete: boolean;
}) {
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const [doDelete, { isLoading: deleting }] = useDelete();

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const closeMenu = (e?: any) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
  };

  const resendInvite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenu(e);
    try {
      await dataProvider.create("users/resendInvite", {
        data: { userId: record.id },
      });

      notify("Einladungslink wurde erneut gesendet.", { type: "info" });
      refresh();
    } catch (err: any) {
      const msg =
        err?.body ? JSON.stringify(err.body, null, 2) : err?.message ?? "Fehler";
      notify(msg, { type: "warning" });
    }
  };

  const askDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenu(e);
    setConfirmOpen(true);
  };

  const cancelDelete = () => setConfirmOpen(false);

  const confirmDelete = () => {
    doDelete(
      "users",
      { id: record.id, previousData: record },
      {
        onSuccess: () => {
          notify("Spieler gelöscht.", { type: "info" });
          setConfirmOpen(false);
          refresh();
        },
        onError: (err: any) => {
          const msg =
            err?.body ? JSON.stringify(err.body, null, 2) : err?.message ?? "Fehler";
          notify(msg, { type: "warning" });
        },
      }
    );
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={openMenu}
        sx={{ flexShrink: 0 }}
        aria-label="Aktionen"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        onClick={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {allowResend && (
          <MenuItem onClick={resendInvite}>
            <ListItemIcon>
              <MailOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Einladung erneut senden" />
          </MenuItem>
        )}

        {allowEdit && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              closeMenu(e);
              redirect("edit", "users", record.id);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Bearbeiten" />
          </MenuItem>
        )}

        {allowDelete && (
          <MenuItem onClick={askDelete}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Löschen" />
          </MenuItem>
        )}
      </Menu>

      <Dialog open={confirmOpen} onClose={cancelDelete}>
        <DialogTitle>Spieler löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Willst du <b>{record.first_name} {record.last_name}</b> wirklich löschen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={deleting}>
            Abbrechen
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ----------------------------- Mobile Cards -------------------------------- */

function UserMobileCards() {
  const { data, isLoading } = useListContext<any>();
  const { permissions, isLoading: permsLoading } = usePermissions();

  if (isLoading || permsLoading) return null;

  const allowEdit = can(permissions, "members", "edit");
  const allowDelete = can(permissions, "members", "delete");
  const allowResend = can(permissions, "members", "resendInvite");

  return (
    <Stack spacing={1} sx={{ p: 1, width: "100%", maxWidth: "100%", overflowX: "clip" }}>
      {(data ?? []).map((record: any) => (

        <Card
          key={record.id}
          variant="outlined"
          sx={{ width: "100%", maxWidth: "100%", overflow: "clip", borderRadius: 1 }}
        >
          <CardContent sx={{ py: 1.25 }}>
            <Stack direction="row" gap={1} alignItems="center" sx={{ width: "100%", minWidth: 0 }}>



              <Box sx={{ flex: 1, minWidth: 0, overflow: "visible" }}>
<PlayerIdentityRow
                        firstName={record.first_name}
                        lastName={record.last_name}
                        email={undefined}
                        points={record.points}
                        avatarUrl={record.avatar_url}
                        showPoints={false}
                      />

                <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                  {record.email}
                </Typography>
              </Box>

              <UserActionsMenu
                record={record}
                allowResend={allowResend}
                allowEdit={allowEdit}
                allowDelete={allowDelete}
              />
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Stack direction="row" justifyContent="space-between" gap={1} sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                Rolle: {record.role ?? "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                Aktiv: {record.is_active ? "Ja" : "Nein"}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

/* -------------------------------- UserList -------------------------------- */

export function UserList() {
  const { clubId } = useClub();
  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));
  const { permissions, isLoading } = usePermissions();
  if (isLoading) return null;

  const allowEdit = can(permissions, "members", "edit");
  const allowDelete = can(permissions, "members", "delete");
  const allowResend = can(permissions, "members", "resendInvite");

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "clip" }}>
      <Typography variant="h5" fontWeight={900} sx={{ px: 2, pt: 2, pb: 1 }}>
        Spieler
      </Typography>

      {isSmall ? (
        <ListBase filter={{ clubId }} queryOptions={{ enabled: !!clubId }} perPage={25}>
          <Box sx={{ overflowX: "clip" }}>
            <TopToolbar sx={{ px: 1, pb: 0.5 }}>
              <ListActions />
            </TopToolbar>

            <MobileSearchBar />
            <UserMobileCards />

            {/* ✅ Mobile Pagination (ListBase rendert das nicht automatisch) */}
            <Box sx={{ px: 1, pb: 1 }}>
              <Pagination />
            </Box>
          </Box>
        </ListBase>
      ) : (
        <List
          filter={{ clubId }}
          filters={userFilters}
          actions={<ListActions />}
          queryOptions={{ enabled: !!clubId }}
          sx={{
            overflowX: "hidden",
            "& .RaList-main": { overflowX: "hidden" },
            "& .RaList-content": { overflowX: "hidden" },
          }}
        >
          <Datagrid rowClick={false} bulkActionButtons={false}>
            <FunctionField
              label="Spieler"
              render={(record: any) => (
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                 <PlayerIdentityRow
                        firstName={record.first_name}
                        lastName={record.last_name}
                        email={undefined}
                        points={record.points}
                        avatarUrl={record.avatar_url}
                        showPoints={false}
                      />
                </Stack>
              )}
            />

            <TextField source="email" />
            <BooleanField source="is_active" label="Aktiv" />
            <TextField source="role" />
            <TextField source="status" />

            {/* ✅ Desktop: Resend Invite Button */}
            {allowResend && (
              <FunctionField
                label=""
                render={(record: any) => <ResendInviteIconButton record={record} />}
              />
            )}

            {allowEdit && <EditButton label="" />}
            {allowDelete && <DeleteButton label="" />}
          </Datagrid>
        </List>
      )}
    </Box>
  );
}
